import React, {useEffect, useState} from 'react';
import {ThemedButton, ThemedDialog, ThemedInput} from "../../../../index";
import {useTheme} from "../../../../../common/ProjectThemeProvider";
import {checkNullStr, focusElement, getJsonValue, isBoolTrue, isJsonValueTrue} from "../../../../../common/fusion-ui/utils/ReactUtils";
import {focusFormIdElement, printError, printLog, sendRequest} from "../../../../utils/AppUtils";
import {useToast} from "../../../../../common/fusion-ui/provider/ToastProvider";
import {ModuleFieldsData, Modules, ReqUrls,} from "../../../../config/AppConfig";
import {Flexbox} from "../../../../../common/fusion-ui";
import {CssVariant, Messages} from "../../../../../common/fusion-ui/config/FusionConfig";
import {useInternetStatus} from "../../../../../common/fusion-ui/hook";
import {UnderMaintenance, UnderMaintenanceMsg} from "../../../../../common/CommonConfig";

const Doc_CreateFolder_FormDialog = React.memo(({
                                                    open,
                                                    onHide,
                                                    onCancel,
                                                    onSuccess,
                                                    onFailure,
                                                }) => {
    const TAG = "Doc_CreateFolder_FormDialog";

    const moduleName = Modules.Documents;
    const FormFields = ModuleFieldsData[moduleName];

    const theme = useTheme();
    const {showToast, changeToastVariant} = useToast();
    const {isOnline, internetCheckingMsg} = useInternetStatus();

    const FOCUS_DELAY = 2000;

    const Fields = {
        submitBtn_AsLoading: 'submitBtn_AsLoading',
        submitBtn_disabled: 'submitBtn_disabled',
    };
    const [fieldsData, setFieldsData] = useState({});
    const [formData, setFormData] = useState({});
    const [formFieldsMsgs, setFormFieldsMsgs] = useState({});

    useEffect(() => {
        setFormData({});
        setFormFieldsMsgs({});
        focusElement(FormFields.folderName.column, 'id');
    }, []);

    return (<>
        <ThemedDialog
            open={isBoolTrue(open)}
            title={'Create new folder'}
            widthClass={'col-sm-10 col-md-4'}
            showClose={false}
            showFooter={false}
            closeOnEscape={true}
            closeOnOutsideClick={false}
            onHide={() => {
                if (onHide) onHide();
                clearPanel();
            }}
            onCancel={() => {
                if (onCancel) onCancel();
                clearPanel();
            }}
            onEscapeClose={() => {
                if (onCancel) onCancel();
                clearPanel();
            }}
        >
            <form onSubmit={handleSubmit}>
                <div className={'px-2'}>
                    <div>
                        {/*folderName*/}
                        <ThemedInput
                            wrapperClassName={''}
                            className={''}
                            label={FormFields.folderName.title}
                            placeholder={FormFields.folderName.title}
                            id={FormFields.folderName.column}
                            type={'text'}
                            value={getFormField(FormFields.folderName)}
                            msg={getFormFieldMsg(FormFields.folderName)}
                            required={true}
                            onChange={(e) => {
                                const value = e.target.value;
                                updateFormField(FormFields.folderName, value);
                                if (checkNullStr(value)) updateFormFieldMsg(FormFields.folderName, '');
                            }}
                        />
                    </div>

                    {/*footer*/}
                    <div className={'mt-1 mb-3'}>
                        {/*controls*/}
                        <Flexbox className={'mt-2'} justifyAt={'center'} alignAt={'center'}>
                            {/*cancel*/}
                            <ThemedButton
                                className={'rounded-2 w-auto px-4 py-1'}
                                text={'Cancel'}
                                variant={theme.text10}
                                hoverBgColor={theme.text30}
                                color={theme.text}
                                onClick={() => {
                                    if (onCancel) onCancel();
                                    if (onHide) onHide();
                                    clearPanel();
                                }}
                                mx={2}
                            />

                            {/*submit*/}
                            <ThemedButton
                                className={'rounded-2 w-auto px-4 py-1'}
                                text={isFieldTrue(Fields.submitBtn_AsLoading) ? 'Creating...' : 'Create'}
                                variant={theme.primaryBlue}
                                hoverBgColor={theme.primaryBlueDark}
                                loadingWidth={'auto'}
                                asLoading={isFieldTrue(Fields.submitBtn_AsLoading)}
                                disabled={isFieldTrue(Fields.submitBtn_disabled)}
                                onClick={handleSubmit}
                                mx={2}
                            />
                        </Flexbox>
                    </div>
                </div>
            </form>
        </ThemedDialog>
    </>)

    function handleSubmit() {
        const fun = 'handleSubmit:';
        log(fun, 'formData:', formData);
        if (UnderMaintenance) {
            toast(UnderMaintenanceMsg, CssVariant.warning);
            return false;
        }
        if (!isOnline) {
            toast(internetCheckingMsg, CssVariant.warning);
            return false;
        }

        try {
            let allFieldsChecked = checkRequiredFields();
            // allFieldsChecked = false;//for checking
            if (allFieldsChecked) {
                updateField(Fields.submitBtn_AsLoading, true);
                updateField(Fields.submitBtn_disabled, true);

                const REQUEST_OPTIONS = {
                    formData: formData,
                };
                sendRequest({
                    reqUrl: ReqUrls.doc__createFolder_submitFormData,
                    reqOptions: REQUEST_OPTIONS,
                    onFetched: ((fetchedData) => {
                        log(fun, 'fetchedData:', fetchedData);

                        const message = getJsonValue(fetchedData, 'message');
                        const success = isJsonValueTrue(fetchedData, 'success');
                        const alreadyExists = isJsonValueTrue(fetchedData, 'alreadyExists');

                        // updateField(Fields.submitBtn_disabled, false);//for testing only
                        updateField(Fields.submitBtn_AsLoading, false);
                        if (success && !alreadyExists) {
                            toast(message, CssVariant.success);
                            if (onSuccess) onSuccess(fetchedData, message);

                            setFieldsData({});
                            setFormData({});
                            setFormFieldsMsgs({});
                        } else {
                            toast(message, CssVariant.danger);
                            updateField(Fields.submitBtn_disabled, false);
                            if (onFailure) onFailure(fetchedData, message);
                        }
                        if (onHide) onHide();
                        clearPanel();
                    }),
                    onError: ((error) => {
                        toast(Messages.saveError, CssVariant.danger);
                        updateField(Fields.submitBtn_AsLoading, false);
                        updateField(Fields.submitBtn_disabled, false);
                        logErr(fun, error);
                    }),
                });
            }
        } catch (error) {
            toast(Messages.saveError, CssVariant.danger);
            updateField(Fields.submitBtn_AsLoading, false);
            updateField(Fields.submitBtn_disabled, false);
            logErr(fun, error);
        }
    }

    //======================Request Functions end=====================

    //======================Util Functions start=====================
    function checkRequiredFields() {
        const fun = 'checkRequiredFields:';
        const requiredFields = [
            FormFields.folderName,
        ];

        let verified = false;
        let colField = {};
        for (const field of requiredFields) {
            if (!checkNullStr(formData[field.column])) {
                colField = field;
                verified = false;
                break;
            } else {
                verified = true;
            }
        }
        if (!verified) {
            // log(fun, "!verified.colField:", colField, 'idToFocus:', colField.column);
            updateFormFieldMsg(colField, 'Required field');
            focusIdField(colField.column);
        }
        return verified;
    }

    function clearPanel() {
        setFormData({});
        setFieldsData({});
        setFormFieldsMsgs({});
    }

    function focusIdField(id, onlyFocus = false) {
        focusFormIdElement(id, onlyFocus, FOCUS_DELAY);
    }

    function updateField(field, value) {
        setFieldsData(prevState => ({...prevState, [field]: value}));
    }

    function getField(field, defValue) {
        return getJsonValue(fieldsData, field, defValue);
    }

    function isFieldTrue(field) {
        return isBoolTrue(getField(field), true, true);
    }

    function updateFormField(clmField, value, direct = false) {
        setFormData(prevState => ({...prevState, [direct ? clmField : clmField.column]: value}));
    }

    function getFormField(clmField, defValue, direct = false) {
        return getJsonValue(formData, direct ? clmField : clmField.column, defValue);
    }

    function isFormFieldTrue(clmField, direct = false) {
        return isBoolTrue(getFormField(clmField, null, direct), true, true);
    }

    function updateFormFieldMsg(clmField, value, direct = false) {
        setFormFieldsMsgs(prevState => ({...prevState, [direct ? clmField : clmField.column]: value}));
    }

    function getFormFieldMsg(clmField, defValue, direct = false) {
        return getJsonValue(formFieldsMsgs, direct ? clmField : clmField.column, defValue);
    }

    function getTabTitle() {
        return `Recommend Video`;
    }

    function toast(text, variant) {
        showToast(text);
        changeToastVariant(variant);
        log(...[text]);
    }

    function log(...text) {
        printLog(TAG, ...text);
    }

    function logErr(...text) {
        printError(TAG, ...text);
    }

    //======================Util Functions end=====================
});
export default Doc_CreateFolder_FormDialog;
